import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { RootState } from "src/store/store"

// Met when the selected room already includes the expected change
export type RoomCondition = (room: any) => boolean

// Combine conditions to build the one each action waits for
export const allOf =
    (...conditions: RoomCondition[]): RoomCondition =>
        (room) =>
            conditions.every((isMet) => isMet(room))

export const anyOf =
    (...conditions: RoomCondition[]): RoomCondition =>
        (room) =>
            conditions.some((isMet) => isMet(room))

// Conditions on the room's unfinished tasks, whether cleaning, supervision or maintenance
const getPendingTasks = (room: any): any[] => room?.colaborador_tareas_sin_finalizar || []

// E.g. after finishing the task
export const withoutPendingTasks: RoomCondition = (room) => !getPendingTasks(room).length

// The pending tasks belong exactly to these collaborators (e.g. after assigning or swapping staff)
export const assignedTo =
    (colaboradorIds: string[]): RoomCondition =>
        (room) => {
            const tasks = getPendingTasks(room)
            return tasks.length === colaboradorIds.length && tasks.every((t) => colaboradorIds.includes(t?.colaborador_id))
        }

// Cleaning only. every is true when there are no tasks: combine it with assignedTo if the tasks must exist
export const withCleaningType =
    (cleaningType: string): RoomCondition =>
        (room) =>
            getPendingTasks(room).every((t) => t?.tipo_limpieza === cleaningType)

// Waits for the selected room to arrive updated after an action, so a skeleton can be shown meanwhile. Ways to wait:
// - waitForRoom(condition): until the room meets the condition. Useful when the change arrives through the
//   RoomsProvider refetch (subscription), which may first deliver responses with the previous data
// - waitWhile(action): while the action runs, e.g. a manual refetch when the action doesn't always trigger the
//   subscription
// - waitForRoom() without a condition: until stopWaiting is called (e.g. while a modal processes the action)
// onWait is called when waiting starts (e.g. to go back to the view that shows the skeleton)
export const useWaitForRoom = (onWait?: () => void) => {
    const room = useSelector((state: RootState) => state.rooms.selectedRoom)
    const [pending, setPending] = useState<{ isMet: RoomCondition } | null>(null)

    // Evaluated every time a new room reaches the store and when waiting starts
    useEffect(() => {
        if (pending?.isMet(room)) {
            setPending(null)
        }
    }, [room, pending])

    const waitForRoom = (isMet: RoomCondition = () => false) => {
        setPending({ isMet })
        onWait?.()
    }

    const stopWaiting = () => setPending(null)

    const waitWhile = async (action: () => Promise<unknown>) => {
        waitForRoom()
        try {
            await action()
        } finally {
            stopWaiting()
        }
    }

    // For mutation-finished callbacks: if it failed, the updated room won't arrive
    const onActionFinished = (success: boolean) => {
        if (!success) {
            stopWaiting()
        }
    }

    return {
        isWaitingForRoom: pending !== null,
        waitForRoom,
        waitWhile,
        stopWaiting,
        onActionFinished,
    }
}
